import iconsPng from '@/assets/img/png/icons';
import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import { LOCATION_STATE_DEFAULT_VALUE } from '@/constants/stringVars';
import useUSStates from '@/hooks/meta/useUSStates';
import useUpdateProviderLocations from '@/hooks/providers/useUpdateProviderLocations';
import colors from '@/theme/colors';
import { TUserLocation } from '@/types/User.types';
import { HStack, Image, Input, Select, Text, VStack } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

const EMPTY_LOCATION = { state: LOCATION_STATE_DEFAULT_VALUE, city: '', zip: '' };

interface Props {
  providerId?: string;
  currentLocations?: TUserLocation[];
  setLocationsValue?: (locations: TUserLocation[]) => void;
  onSuccess?: () => void;
}

const LABEL_INPUT_VERTICAL_GAP = '10px';

const ProviderLocationUpdateModalWithTrigger = ({
  providerId,
  currentLocations,
  setLocationsValue,
  onSuccess,
}: Props) => {
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);

  const isEdit = !!providerId;

  const { data: USStates } = useUSStates();

  const { mutate: updateLocations, isPending: isLocationUpdatePending } = useUpdateProviderLocations(onSuccess);

  const onClickUpdate = () => {
    const values = getValues();

    if (isEdit) {
      updateLocations({ providerId: providerId, locations: values.locations });
    } else {
      setLocationsValue?.(values.locations);
    }

    setIsLocationModalVisible(false);
  };

  const { register, watch, formState, control, getValues } = useForm<{ locations: TUserLocation[] }>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      locations: currentLocations && currentLocations.length > 0 ? currentLocations : [EMPTY_LOCATION],
    },
  });
  const { isValid: isValidInputFields, errors, touchedFields } = formState;

  const {
    fields: providerLocationFields,
    append,
    remove,
  } = useFieldArray<{ locations: TUserLocation[] }>({
    control,
    name: 'locations',
  });

  const locationsValue = watch('locations');

  const sortedCurrentLocations = useMemo(
    () => currentLocations?.sort((locA, locB) => (locA.state < locB.state ? -1 : 1)),
    [currentLocations],
  );

  const sortedLocationsValue = useMemo(
    () => locationsValue?.sort((locA, locB) => (locA.state < locB.state ? -1 : 1)),
    [locationsValue],
  );

  const locationsChanged = JSON.stringify(sortedCurrentLocations) !== JSON.stringify(sortedLocationsValue);

  return (
    <VStack width={'100%'} marginY={'20px'}>
      {currentLocations && currentLocations?.length > 0 ? (
        <HStack marginBottom={'10px'} className="custom-input" data-state="valid" width={'100%'}>
          <Text variant={'urbanistSemiBold'}>
            {currentLocations.length} Location{currentLocations.length > 1 ? 's' : ''}:{' '}
          </Text>

          <Text variant={'urbanistSemiBold'}>{currentLocations?.map((location) => location.state).join(', ')}</Text>
        </HStack>
      ) : (
        <Text variant={'urbanistSemiBold'} margin={'10px'} className="custom-input" lineHeight={'22px'}>
          No Locations selected yet.
        </Text>
      )}

      <CustomButton
        backgroundColor="secondary.500"
        label={`${currentLocations && currentLocations?.length > 0 ? 'Update' : 'Add'} Locations`}
        width={FORM_FIELD_WIDTH}
        height={'50px'}
        onClick={() => setIsLocationModalVisible(true)}
      />

      <ModalContainer
        isOpen={isLocationModalVisible}
        onClose={() => {
          setIsLocationModalVisible(false);
        }}
        title="Locations"
        width={'720px'}
        backgroundColor={'background.lightBlue'}
      >
        <VStack width={'550px'} marginBottom={'36px'} alignItems={'center'} marginTop={'36px'}>
          <VStack gap={'40px'} width={'100%'}>
            <Image alt="ok-hand" src={iconsPng.locationPin} width={100} height={100} />
            <Text variant={'urbanistSemiBoldLarge'} color={'text.mediumBlue'} width={'400px'} textAlign={'center'}>
              We use your location to connect you with nearby members, ensuring convenient access to support. This helps
              us match you with members requiring for either telehealth or in-person sessions based on your preference
              and location.
            </Text>

            <VStack position={'relative'} marginBottom={'30px'} gap={'20px'}>
              {/* Add location input row */}
              <VStack position={'absolute'} bottom={'-10px'} data-group>
                <VStack
                  position={'absolute'}
                  top={'-20px'}
                  height={'54px'}
                  width={'100%'}
                  backgroundColor={'yellow'}
                  justifyContent={'center'}
                >
                  <CustomButton
                    isTransparent
                    onClick={() => append(EMPTY_LOCATION)}
                    iconBeforeLabelSvgSize={15}
                    iconBeforeLabelSvgPath={svgIconPaths.plus}
                    iconBeforeLabelSvgColor={colors.text.darkBlue}
                    width={'30px'}
                    height={'30px'}
                    disabledColor="transparent"
                    borderHoverColor="transparent"
                    labelStyle={{ marginLeft: 0, marginTop: '6px' }}
                    style={{ minWidth: '20px', borderRadius: '50%', paddingInline: 0 }}
                  />
                  <Text
                    _groupHover={{ visibility: 'visible' }}
                    visibility={'hidden'}
                    variant={'urbanistSemiBoldRegular'}
                    position={'absolute'}
                    width={'170px'}
                    left={'35px'}
                    top={'15px'}
                    padding={'10px'}
                    borderRadius={'8px'}
                    backgroundColor={'extra.white'}
                    boxShadow={'0px 6.97px 34.86px 0px #00417930'}
                  >
                    Adds one more location
                  </Text>
                </VStack>
              </VStack>

              {providerLocationFields?.map((field, index) => {
                const locationValueIsDefault = locationsValue?.[index].state === LOCATION_STATE_DEFAULT_VALUE;

                return (
                  <VStack key={field.id} alignItems={'center'} width={'100%'}>
                    <HStack spacing={'20px'} minWidth={'100%'} position={'relative'}>
                      <VStack position={'absolute'} width={'100%'} backgroundColor={'green'}>
                        {/* Remove location input row */}
                        <VStack position={'absolute'} right={0} height={'30px'} justifyContent={'center'} data-group>
                          <CustomButton
                            isTransparent
                            onClick={() => remove(index)}
                            iconBeforeLabelSvgSize={15}
                            iconBeforeLabelSvgPath={svgIconPaths.closeSmall}
                            iconBeforeLabelSvgColor={colors.extra.red}
                            width={'30px'}
                            height={'30px'}
                            disabledColor="transparent"
                            borderHoverColor="transparent"
                            labelStyle={{ marginLeft: 0, marginTop: '6px' }}
                            style={{ minWidth: '20px', borderRadius: '50%', paddingInline: 0 }}
                            disabled={locationsValue.length === 1}
                          />
                          {locationsValue.length === 1 && (
                            <Text
                              _groupHover={{ visibility: 'visible' }}
                              visibility={'hidden'}
                              variant={'urbanistSemiBoldRegular'}
                              position={'absolute'}
                              width={'100px'}
                              left={'5px'}
                              top={'35px'}
                              padding={'10px'}
                              borderRadius={'8px'}
                              backgroundColor={'extra.white'}
                              boxShadow={'0px 6.97px 34.86px 0px #00417930'}
                            >
                              You must have at least 1 location.
                            </Text>
                          )}
                        </VStack>
                      </VStack>

                      <VStack flex={1} alignItems={'flex-start'} gap={LABEL_INPUT_VERTICAL_GAP}>
                        <Text variant={'urbanistBoldLarge'}>Location</Text>
                        <HStack>
                          <Input
                            placeholder="City"
                            className="custom-input"
                            _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 12 }}
                            color={'text.darkGray'}
                            isInvalid={!!errors?.locations?.[index]?.city}
                            {...register(`locations.${index}.city`, {
                              required: { message: 'City is required', value: true },
                              minLength: { value: 2, message: 'City must have at least 2 characters' },
                              pattern: { value: /^[a-zA-Z\s]*$/, message: 'City can only contain letters and spaces' },
                            })}
                            data-state={
                              touchedFields.locations?.[index]?.city
                                ? errors.locations?.[index]?.city
                                  ? 'invalid'
                                  : 'valid'
                                : ''
                            }
                          />

                          <Input
                            placeholder="ZIP Code"
                            className="custom-input"
                            _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 13 }}
                            color={'text.darkGray'}
                            isInvalid={!!errors?.locations?.[index]?.zip}
                            {...register(`locations.${index}.zip`, {
                              required: { message: 'ZIP Code is required', value: true },
                              pattern: { value: /^\d{5}(-\d{4})?(?!-)$/, message: 'Invalid ZIP Code' },
                            })}
                            minWidth={'70px'}
                            data-state={
                              touchedFields.locations?.[index]?.zip
                                ? errors.locations?.[index]?.zip
                                  ? 'invalid'
                                  : 'valid'
                                : ''
                            }
                          />
                        </HStack>
                      </VStack>
                      <HStack flex={0.85} spacing={'10px'} marginRight={'40px'}>
                        <VStack alignItems={'flex-start'} flex={1} gap={LABEL_INPUT_VERTICAL_GAP}>
                          <Text variant={'urbanistBoldLarge'} visibility={'hidden'}>
                            _
                          </Text>
                          <HStack minWidth={'100%'} width={'100%'}>
                            <Select
                              minWidth={'100%'}
                              className="custom-input custom-select-padding"
                              defaultValue={LOCATION_STATE_DEFAULT_VALUE}
                              isInvalid={touchedFields.locations?.[index]?.state && locationValueIsDefault}
                              style={
                                locationValueIsDefault
                                  ? {
                                      color: colors.text.mediumGray,
                                      fontSize: '14px',
                                      borderColor: touchedFields.locations?.[index]?.state
                                        ? colors.extra.lightRed
                                        : '#e2e8f0',
                                    }
                                  : {}
                              }
                              {...register(`locations.${index}.state`, {
                                required: { message: 'State is required', value: true },
                                validate: (value: string) =>
                                  value === LOCATION_STATE_DEFAULT_VALUE ? 'State is required' : true,
                              })}
                              data-state={
                                touchedFields.locations?.[index]?.state
                                  ? locationValueIsDefault
                                    ? 'invalid'
                                    : 'valid'
                                  : ''
                              }
                            >
                              <option disabled>{LOCATION_STATE_DEFAULT_VALUE}</option>
                              {(USStates ?? []).map((USState) => (
                                <option key={USState.label} value={USState.code}>
                                  {USState.label}
                                </option>
                              ))}
                            </Select>
                          </HStack>
                        </VStack>
                      </HStack>
                    </HStack>

                    <Text variant={'error'}>
                      {errors?.locations?.[index]?.city?.message ||
                        errors?.locations?.[index]?.zip?.message ||
                        errors?.locations?.[index]?.state?.message ||
                        ''}
                    </Text>
                  </VStack>
                );
              })}
            </VStack>

            <CustomButton
              width={FORM_FIELD_WIDTH}
              height={'54px'}
              onClick={onClickUpdate}
              backgroundColor="secondary.500"
              labelColor="extra.white"
              label="Update"
              disabled={!isValidInputFields || !locationsChanged}
              isLoading={isLocationUpdatePending}
            />
          </VStack>
        </VStack>
      </ModalContainer>
    </VStack>
  );
};

export default ProviderLocationUpdateModalWithTrigger;
